import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config/constants';
import errorHandler from './errorHandler';
import navigationService from './navigationService';
import { emitDevOtpFromResponse } from '../utils/devOtp';

// پیام پیش‌فرض برای حالتی که بک‌اند پیام فارسی برنگرداند (کد OUT_OF_SHIFT)
const OUT_OF_SHIFT_FALLBACK_MESSAGE =
  'شما در حال حاضر خارج از شیفت کاری خود هستید و امکان انجام این عملیات را ندارید.';

/**
 * نمایش غیرمخرب پیام «خارج از شیفت» — بدون ریدایرکت، فقط toast.
 * toastId از نمایش چندباره‌ی هم‌زمان (چند درخواست موازی) جلوگیری می‌کند.
 */
const notifyOutOfShift = (message?: string) => {
  toast.warning(message || OUT_OF_SHIFT_FALLBACK_MESSAGE, { toastId: 'out-of-shift' });
};

// Create a custom axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // افزودن تایم‌اوت برای درخواست‌ها
  timeout: 30000
});

// Request interceptor for adding the auth token and permissions
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // (The old `X-User-Permissions` header was removed: it was fed from a
    // stale localStorage blob and the backend never read it — permissions
    // are resolved server-side from roles/grants/shift on every request.)
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token refresh and permission checks
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    emitDevOtpFromResponse(response.data);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    // Check if the error is related to an API call (could be network error)
    if (!error.response) {
      console.error('Network error or no response:', error.message);
      return Promise.reject(error);
    }
    
    const status = error.response.status;
    console.log(`API Error: ${status} on ${originalRequest?.url}`, error.response.data);
    
    // بررسی مسیرهای احراز هویت - عدم تلاش برای رفرش توکن در مسیرهای لاگین/ثبت‌نام
    const isAuthEndpoint = originalRequest?.url && (
      originalRequest.url.includes('/auth/login') || 
      originalRequest.url.includes('/auth/admin/login') ||
      originalRequest.url.includes('/auth/register') ||
      originalRequest.url.includes('/auth/verify-otp') ||
      originalRequest.url.includes('/auth/admin/verify-otp') ||
      originalRequest.url.includes('/admin/auth/refresh') ||
      originalRequest.url.includes('/auth/forgot-password') ||
      originalRequest.url.includes('/auth/reset-password')
    );
    
    // If error is 401 (Unauthorized) and it's not an auth endpoint
    if (status === 401 && !isAuthEndpoint && originalRequest) {
      console.log('401 Unauthorized response received:', originalRequest.url);
      
      // Check if this is already a retry attempt
      if ((originalRequest as any)._retry) {
        console.log('Token refresh already attempted and failed.');
        
        // استفاده از سیستم ریدایرکت جدید
        navigationService.redirectToLogin();
        return Promise.reject(error);
      }
      
      (originalRequest as any)._retry = true;
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.warn('No refresh token available.');
          
          // استفاده از سیستم ریدایرکت جدید
          navigationService.redirectToLogin();
          return Promise.reject(error);
        }
        
        console.log('Attempting to refresh token...');
        // Call the ADMIN refresh endpoint. ⚠️ It is `/admin/auth/refresh`
        // (routes/admin.routes.ts mounted under /admin/auth). The old URL
        // `/auth/admin/refresh-token` never existed → every 401 (e.g. the
        // tokenVersion bump after a role/grant change) logged the admin out.
        const response = await axios.post(`${API_BASE_URL}/admin/auth/refresh`, {
          refreshToken
        });

        // Accept both response shapes: `data.tokens.{accessToken,refreshToken}`
        // and the flat `data.accessToken`. The refresh token is not rotated by
        // the backend, so keep the current one when none comes back.
        const body = response.data?.data || {};
        const newAccess: string | undefined = body.tokens?.accessToken || body.accessToken;
        const newRefresh: string | undefined = body.tokens?.refreshToken || body.refreshToken;
        if (newAccess) {
          console.log('Token successfully refreshed');

          // Store new tokens
          localStorage.setItem('accessToken', newAccess);
          if (newRefresh) localStorage.setItem('refreshToken', newRefresh);

          // Set header for retry
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          }
          
          // Retry the original request with a type assertion to avoid TypeScript error
          return apiClient(originalRequest as any);
        } else {
          console.warn('Refresh token response did not contain new tokens:', response.data);
          
          // استفاده از سیستم ریدایرکت جدید
          navigationService.redirectToLogin();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        
        // استفاده از سیستم ریدایرکت جدید
        navigationService.redirectToLogin();
        return Promise.reject(error);
      }
    }
    
    // اگر خطای 403 است، به صفحه عدم دسترسی ریدایرکت کن — مگر اینکه درخواست صراحتاً
    // opt-out کرده باشد (silentForbidden). برای فراخوان‌های پس‌زمینه‌ی داشبورد لازم است
    // که فقط همان بخش پنهان شود و کاربر از صفحه بیرون پرتاب/ریدایرکت نشود.
    // استثنا: کد OUT_OF_SHIFT یعنی ادمین دسترسی دارد ولی خارج از شیفت است؛
    // در این حالت ریدایرکت نمی‌کنیم و فقط پیام فارسی بک‌اند را نمایش می‌دهیم.
    if (status === 403 && !isAuthEndpoint && !(originalRequest as any)?.silentForbidden) {
      const responseBody = error.response.data as any;
      if (responseBody?.code === 'OUT_OF_SHIFT') {
        notifyOutOfShift(responseBody?.message);
      } else {
        navigationService.redirectToUnauthorized();
      }
    }
    
    return Promise.reject(error);
  }
);

// Check if the API server is running
export const checkApiServer = async (): Promise<boolean> => {
  try {
    console.log('🔍 Checking if API server is running at:', API_BASE_URL);
    
    // First try with authentication token
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Try to connect to admin APIs as a proxy for health check
    // Since we know the health endpoint is getting a 404
    const response = await fetch(`${API_BASE_URL}/admin/account-types?page=1&limit=1`, { 
      method: 'GET',
      headers
    });
    
    console.log(`🔍 API server connection test status: ${response.status}`);
    
    // If we get 401 or 200, the server is definitely running
    if (response.status === 200 || response.status === 401) {
      console.log('🔍 Server is running - connection test successful');
      return true;
    }
    
    // Any response means the server is running, even if it's an error
    return true;
  } catch (error) {
    console.error('🔍 API server is not accessible:', error);
    return false;
  }
};

// Add special logging for seller application routes
// When we detect a GET request to one of these paths, log more information
const sellerApplicationPaths = [
  '/admin/sellers/applications',
  '/test/sellers/applications',
  '/test/sellers/applications-mock'
];

// Add withdrawal paths that need full response structure
const withdrawalPaths = [
  '/admin/withdrawals'
];

// API service methods
const apiService = {
  // Added method to check server status
  checkServer: async (): Promise<boolean> => {
    return await checkApiServer();
  },

  // Generic HTTP methods
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      // Add special logging for seller application routes
      const isSellerAppRoute = sellerApplicationPaths.some(path => url.includes(path));
      const isWithdrawalRoute = withdrawalPaths.some(path => url.includes(path));
      
      if (isSellerAppRoute) {
        console.log(`🔍 Making GET request to seller application route: ${url}`);
        
        // Skip pre-flight HEAD request as it's causing unnecessary 401 responses
        // and the main GET request works fine
      }
      
      if (isWithdrawalRoute) {
        console.log(`🔍 Making GET request to withdrawal route: ${url}`);
      }
      
      const response = await apiClient.get<any>(url, config);
      
      if (isSellerAppRoute) {
        console.log(`🔍 Seller app response status: ${response.status}, data structure:`, 
          Object.keys(response.data || {}));
      }
      
      if (isWithdrawalRoute) {
        console.log(`🔍 Withdrawal response status: ${response.status}, data structure:`, 
          Object.keys(response.data || {}));
        console.log(`🔍 Full withdrawal response:`, response.data);
      }
      
      // If the response has a nested data structure with a 'data' field, extract it
      if (response.data && typeof response.data === 'object') {
        // Handle response format: { status: 'success', data: {...} }
        if ('status' in response.data && 'data' in response.data) {
          // Additional check for response format variations
          if (isSellerAppRoute) {
            console.log(`Seller application response format detected: 
              status=${response.data.status}, 
              has data object=${typeof response.data.data === 'object'}, 
              has docs array=${Array.isArray(response.data.data?.docs)}`);
          }
          
          // For seller application routes, keep the full response structure
          if (isSellerAppRoute) {
            console.log(`Keeping full response structure for seller applications`);
            return response.data as T;
          }
          
          // For withdrawal routes, keep the full response structure
          if (isWithdrawalRoute) {
            console.log(`Keeping full response structure for withdrawal routes`);
            return response.data as T;
          }
          
          // Additional check for new API format with results field
          if ('results' in response.data && Array.isArray(response.data.data)) {
            console.log(`Extracting array data with ${response.data.results} items from API response`);
            return response.data.data as T;
          }
          
          console.log(`Extracting nested data from API response:`, response.data.data);
          return response.data.data as T;
        }
      }
      
      return response.data as T;
    } catch (error) {
      // For seller app routes, log more details about the error
      const isSellerAppRoute = sellerApplicationPaths.some(path => url.includes(path));
      const isWithdrawalRoute = withdrawalPaths.some(path => url.includes(path));
      
      if (isSellerAppRoute) {
        console.error(`🔍 Error in seller application request to ${url}:`, error);
        
        // Try to get more connection info
        try {
          fetch(API_BASE_URL + '/health').catch(e => 
            console.log('Health check failed', e)
          );
        } catch (e) {}
      }
      
      if (isWithdrawalRoute) {
        console.error(`🔍 Error in withdrawal request to ${url}:`, error);
      }
      
      throw handleApiError(error);
    }
  },
  
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await apiClient.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await apiClient.put<any>(url, data, config);

      // Handle nested data structure similar to get method
      if (response.data && typeof response.data === 'object') {
        // Handle response format: { status: 'success', data: {...} }
        if ('status' in response.data && 'data' in response.data) {
          return response.data.data as T;
        }
      }

      return response.data as T;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await apiClient.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    try {
      const response = await apiClient.delete<T>(url, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // Request with pagination
  getPaginated: async <T>(url: string, params: {
    page?: number;
    limit?: number;
    search?: string;
    [key: string]: any;
  }): Promise<{
    items: T[];
    meta: {
      totalItems: number;
      itemCount: number;
      itemsPerPage: number;
      totalPages: number;
      currentPage: number;
    }
  }> => {
    try {
      // Use params as is - our components already use 1-indexed page numbers
      const adjustedParams = { ...params };
      // Remove the automatic page adjustment that was causing issues
      
      const response = await apiClient.get(url, { params: adjustedParams });
      
      // Transform the API response to match the expected format
      const responseData = response.data;
      
      console.log('API pagination response:', JSON.stringify(responseData));
      
      // Handle new API format with status, results, pagination, and data fields
      if (responseData.status === 'success' && Array.isArray(responseData.data)) {
        // Transform data to ensure each item has an 'id' property (using _id if needed)
        const transformedItems = responseData.data.map((item: any) => {
          if (item.id) {
            return item;
          }
          // If the item has _id but not id, add id property
          if (item._id) {
            return { ...item, id: item._id };
          }
          return item;
        });
        
        return {
          items: transformedItems,
          meta: {
            totalItems: responseData.pagination?.total || 0,
            itemCount: responseData.data.length,
            itemsPerPage: responseData.pagination?.limit || 10,
            totalPages: responseData.pagination?.pages || 1,
            currentPage: responseData.pagination?.page || 1
          }
        };
      }
      
      // Handle old API format structure (if it still exists)
      if (responseData.data && responseData.data.items && Array.isArray(responseData.data.items)) {
        return responseData.data;
      }
      
      // Fallback for direct array response
      if (Array.isArray(responseData)) {
        return {
          items: responseData,
          meta: {
            totalItems: responseData.length,
            itemCount: responseData.length,
            itemsPerPage: params.limit || 10,
            totalPages: 1,
            currentPage: 1
          }
        };
      }
      
      // Default fallback
      console.warn('Unrecognized API response format:', responseData);
      return {
        items: [],
        meta: {
          totalItems: 0,
          itemCount: 0,
          itemsPerPage: params.limit || 10,
          totalPages: 0,
          currentPage: params.page || 0
        }
      };
    } catch (error) {
      console.error('Error in getPaginated:', error);
      throw handleApiError(error);
    }
  }
};

// Helper to handle API errors
const handleApiError = (error: any): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    
    // Extract response data if available
    const responseData = axiosError.response?.data as any;
    
    // بررسی کد وضعیت HTTP و مدیریت آن
    if (axiosError.response) {
      const status = axiosError.response.status;
      
      console.log(`API Error Status: ${status}, URL: ${axiosError.config?.url}`);
      
      // ریدایرکت فقط برای 401 و 403 - ساده‌ترین شکل ممکن
      if (status === 401) {
        console.log('401 Unauthorized - Using new direct redirect system');
        // استفاده از سیستم ریدایرکت جدید
        navigationService.redirectToLogin();
        return new Error('دسترسی غیرمجاز. لطفاً دوباره وارد شوید');
      }
      
      if (status === 403) {
        // OUT_OF_SHIFT: ادمین دسترسی دارد ولی خارج از شیفت است — ریدایرکت نکن؛
        // پیام فارسی بک‌اند را به فراخوان‌گر برگردان (toast در interceptor نمایش داده شده).
        if (responseData?.code === 'OUT_OF_SHIFT') {
          return new Error(responseData?.message || OUT_OF_SHIFT_FALLBACK_MESSAGE);
        }
        // درخواست‌هایی که silentForbidden ست کرده‌اند (مثل ویجت‌های داشبورد) نباید
        // کاربر را ریدایرکت کنند؛ فقط خطا برگردانده می‌شود تا فراخوان‌گر همان بخش را پنهان کند.
        if (!(axiosError.config as any)?.silentForbidden) {
          console.log('403 Forbidden - Using new direct redirect system');
          navigationService.redirectToUnauthorized();
        }
        return new Error('شما دسترسی لازم برای انجام این عملیات را ندارید');
      }
      
      // Handle other HTTP status codes
      switch (status) {
        case 404:
          return new Error('منبع مورد نظر یافت نشد');
        case 409:
          return new Error(responseData?.message || 'تداخل در عملیات. این داده قبلاً ثبت شده است');
        case 422:
          return new Error(responseData.message || 'اطلاعات وارد شده معتبر نیست');
        case 500:
          alert('خطا در سرور. لطفاً بعداً تلاش کنید');
          return new Error('خطا در سرور. لطفاً بعداً تلاش کنید');
        default:
          return new Error(responseData.message || 'خطا در ارتباط با سرور');
      }
    }
    
    // Try different data structures that API might return
    if (responseData) {
      const errorMessage = 
        responseData.message || 
        (responseData.error && typeof responseData.error === 'string' ? responseData.error : null) ||
        (typeof responseData === 'string' ? responseData : null) ||
        axiosError.message ||
        'خطا در ارتباط با سرور';
      
      return new Error(errorMessage);
    }
    
    // If no response data available
    if (axiosError.code === 'ECONNABORTED') {
      return new Error('زمان درخواست به پایان رسید. لطفاً اتصال خود را بررسی کنید');
    }
    
    if (axiosError.code === 'ERR_NETWORK') {
      return new Error('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید');
    }
    
    return new Error(axiosError.message || 'خطا در ارتباط با سرور');
  }
  
  // For non-Axios errors
  return error instanceof Error ? error : new Error('خطای ناشناخته');
};

/**
 * The raw axios instance (auth header + 401 refresh + 403/OUT_OF_SHIFT handling)
 * for pages that want plain axios semantics (`res.data`) instead of the
 * unwrapping helpers above. Prefer this over `import axios` + a hand-built
 * Authorization header — that path gets none of the interceptors.
 */
export const http = apiClient;

export default apiService;